import {
  WEBINAR_GOOD_LEAD_SCORE,
  type WebinarAttendee,
} from "@xop/dashboard-shared";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState, type JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useCan } from "../../../../auth/permissions.js";
import { toast } from "../../../../components/ui/use-toast.js";
import { webinarsApi } from "../client.js";
import { DoneNote, StepFooter, StepIntro, type StepProps } from "../stepShared.js";

/**
 * Step 5 — find the leads worth chasing.
 *
 * Someone who gave us their name and then sat through a 45-minute session on
 * emergency communications is warmer than anything a cold harvester finds.
 * But a 200-row attendee list is still mostly noise, so the AI scores each
 * person 0-100 with a reason, the table sorts by that score, and the operator
 * pushes the good ones into the CRM as prospects for sales to chase.
 *
 * The selection defaults to "everyone the AI rated well who is not already in
 * the CRM" — the operator confirms a recommendation rather than assembling
 * one from scratch.
 */
export function StepLeads({ detail, refresh, canEdit, markDone }: StepProps): JSX.Element {
  const { webinar, attendees } = detail;
  const canReachCrm = useCan("crm:view");
  const [selected, setSelected] = useState<Set<number> | null>(null);

  const joined = useMemo(() => attendees.filter((a) => a.joined), [attendees]);
  const scoredCount = joined.filter((a) => a.lead_score !== null).length;
  const pushedCount = joined.filter((a) => a.lead_state === "pushed").length;

  // Default selection: the AI's recommendation — everyone at or above the
  // shared "good lead" bar who is neither already in the CRM nor explicitly
  // marked "not a lead". `null` means "the operator has not touched this yet",
  // so a re-score refreshes it instead of freezing a stale pick.
  const recommended = useMemo(
    () =>
      new Set(
        joined
          .filter(
            (a) =>
              a.lead_state !== "pushed" &&
              a.lead_state !== "skipped" &&
              (a.lead_score ?? 0) >= WEBINAR_GOOD_LEAD_SCORE,
          )
          .map((a) => a.id),
      ),
    [joined],
  );
  // Guard the operator's own selection too: a person they ticked and then
  // marked "not a lead" must not ride along on the next push.
  const selectable = useMemo(
    () => new Set(joined.filter((a) => a.lead_state === "unscored" || a.lead_state === "scored").map((a) => a.id)),
    [joined],
  );
  const picked = useMemo(
    () => new Set([...(selected ?? recommended)].filter((id) => selectable.has(id))),
    [selected, recommended, selectable],
  );

  const score = useMutation({
    mutationFn: () => webinarsApi.scoreLeads(webinar.id),
    onSuccess: (res) => {
      setSelected(null);
      refresh();
      toast({
        title: `Scored ${res.scored} of ${res.of}`,
        ...(res.warnings?.length ? { description: res.warnings[0] } : {}),
      });
    },
    onError: (e: Error) =>
      toast({ title: "Could not score the list", description: e.message, variant: "destructive" }),
  });

  const push = useMutation({
    mutationFn: () => webinarsApi.pushLeads(webinar.id, [...picked]),
    onSuccess: (res) => {
      setSelected(null);
      refresh();
      if (res.failed.length) {
        toast({
          title: `${res.pushed} sent to the CRM, ${res.failed.length} could not be`,
          description: res.failed[0]?.reason ?? "",
          variant: "destructive",
        });
      } else if (res.already_in_crm > 0) {
        // Not a failure, and worth saying out loud — otherwise "sent 1 of 3"
        // reads as two things going wrong.
        toast({
          title: `${res.pushed} sent to the CRM`,
          description:
            `${res.already_in_crm} ${res.already_in_crm === 1 ? "was" : "were"} already there, ` +
            "so they were linked to the existing record instead of added again.",
        });
      } else {
        toast({
          title: `${res.pushed} sent to the CRM`,
          description: "They are prospects now - sales can pick them up.",
        });
      }
    },
    onError: (e: Error) =>
      toast({ title: "Could not send to the CRM", description: e.message, variant: "destructive" }),
  });

  // "Not a lead" — the operator looked at someone and decided no. Recorded
  // rather than just left unticked, so the decision survives a re-score and a
  // second person working the list does not re-litigate it.
  const skip = useMutation({
    mutationFn: (attendeeId: number) => webinarsApi.skipLeads(webinar.id, [attendeeId]),
    onSuccess: () => refresh(),
    onError: (e: Error) =>
      toast({ title: "Could not update", description: e.message, variant: "destructive" }),
  });

  const toggle = (id: number): void => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  return (
    <Box>
      <StepIntro>
        Find leads among the people who joined. The AI reads every attendee and rates how
        worthwhile it is for sales to contact them; pick the good ones and send them to the CRM.
        Anyone we already have is linked to the existing record rather than added twice.
      </StepIntro>

      {joined.length === 0 ? (
        <Alert severity="info">
          Nobody has been marked as having joined yet. Go back to step 2 and add the attendee
          list.
        </Alert>
      ) : (
        <Stack spacing={2.5}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={
                score.isPending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
              }
              onClick={() => score.mutate()}
              disabled={!canEdit || score.isPending}
              data-testid="score-leads"
            >
              {score.isPending
                ? "Reading the list…"
                : scoredCount > 0
                  ? "Score them again"
                  : `Score all ${joined.length} people`}
            </Button>
            {scoredCount > 0 && (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {scoredCount} of {joined.length} scored
                {pushedCount > 0 ? ` · ${pushedCount} already in the CRM` : ""}
              </Typography>
            )}
          </Box>

          {scoredCount > 0 && (
            <>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: 420,
                  overflow: "auto",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Score</TableCell>
                      <TableCell>Who</TableCell>
                      <TableCell>Why the AI says so</TableCell>
                      <TableCell>What to do</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {joined.map((a) => (
                      <LeadRow
                        key={a.id}
                        attendee={a}
                        checked={picked.has(a.id)}
                        onToggle={() => toggle(a.id)}
                        onSkip={() => skip.mutate(a.id)}
                        canEdit={canEdit}
                        canPush={canEdit && canReachCrm}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {canReachCrm ? (
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={() => push.mutate()}
                    disabled={!canEdit || picked.size === 0 || push.isPending}
                    data-testid="push-leads"
                  >
                    {push.isPending
                      ? "Sending…"
                      : `Send ${picked.size} ${picked.size === 1 ? "person" : "people"} to the CRM`}
                  </Button>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    They become prospects, with the webinar and the AI&apos;s reasoning in the
                    notes.
                  </Typography>
                </Box>
              ) : (
                <Alert severity="warning">
                  Sending people to the CRM needs CRM access. Ask an administrator, or hand the
                  list to someone on the sales team.
                </Alert>
              )}

              {pushedCount > 0 && (
                <DoneNote>
                  {pushedCount} {pushedCount === 1 ? "person is" : "people are"} in the CRM.{" "}
                  <RouterLink to="/crm/prospects">Open Prospects</RouterLink>
                </DoneNote>
              )}
            </>
          )}
        </Stack>
      )}

      <StepFooter
        step="leads"
        detail={detail}
        markDone={markDone}
        canEdit={canEdit}
        {...(scoredCount === 0 ? { disabledReason: "Score the list first." } : {})}
      />
    </Box>
  );
}

function LeadRow({
  attendee,
  checked,
  onToggle,
  onSkip,
  canEdit,
  canPush,
}: {
  attendee: WebinarAttendee;
  checked: boolean;
  onToggle: () => void;
  onSkip: () => void;
  /** May act on the list at all (`marketing:edit`). */
  canEdit: boolean;
  /** May also send someone to the CRM (`marketing:edit` + `crm:view`). */
  canPush: boolean;
}): JSX.Element {
  const pushed = attendee.lead_state === "pushed";
  const skipped = attendee.lead_state === "skipped";
  const score = attendee.lead_score;

  return (
    <TableRow data-testid={`lead-row-${attendee.id}`} hover>
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          checked={checked && !pushed && !skipped}
          disabled={pushed || skipped || !canPush || score === null}
          onChange={onToggle}
          inputProps={{ "aria-label": `Select ${attendee.email}` }}
        />
      </TableCell>
      <TableCell>
        {score === null ? (
          <Chip size="small" variant="outlined" label="not scored" />
        ) : (
          <Chip
            size="small"
            color={score >= WEBINAR_GOOD_LEAD_SCORE ? "success" : "default"}
            variant={score >= WEBINAR_GOOD_LEAD_SCORE ? "filled" : "outlined"}
            label={score}
          />
        )}
      </TableCell>
      <TableCell>
        <Typography sx={{ fontSize: 14 }}>{attendee.name || attendee.email}</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {[attendee.job_title, attendee.company].filter(Boolean).join(" · ") || attendee.email}
        </Typography>
        {pushed && <Chip size="small" color="primary" label="In the CRM" sx={{ mt: 0.5 }} />}
        {skipped && <Chip size="small" variant="outlined" label="Not a lead" sx={{ mt: 0.5 }} />}
      </TableCell>
      <TableCell sx={{ fontSize: 13, maxWidth: 320 }}>{attendee.lead_reason || "-"}</TableCell>
      <TableCell sx={{ fontSize: 13, maxWidth: 220 }}>
        {attendee.lead_recommendation || "-"}
      </TableCell>
      <TableCell align="right">
        {!pushed && !skipped && canEdit && (
          <Button
            size="small"
            color="inherit"
            onClick={onSkip}
            data-testid={`skip-lead-${attendee.id}`}
          >
            Not a lead
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
